import { apiFetch } from "@/lib/auth-api";

/**
 * Moduł „Drukarki w aptekach” — typy i wywołania API. Kształty odpowiedzi to
 * 1:1 sekcja „Kształty odpowiedzi” kontraktu `doc/urzadzenia/drukarki.md`;
 * trzymaj je wyłącznie tutaj, żeby wyrównanie z backendem było jedną zmianą.
 * Ścieżki BEZ `/api` — prefiks, token i nagłówek CSRF dokłada `apiFetch`.
 */

// GET /device-agent/printers/pharmacies
export type PrinterPharmacy = {
	idfirm: number;
	pharmacyName: string | null;
	deviceCount: number;
	onlineCount: number;
	supportedCount: number;
	cablePrinterCount: number;
	activeShareCount: number;
};
/** Grupa urządzeń bez apteki (`idfirm = NULL`) — rola urządzenia. */
export type PrinterGroupId = "office_laptop" | "natubay";
export type PrinterGroup = {
	group: PrinterGroupId;
	/** Nazwa grupy z backendu („Laptopy biurowe”, „Natubay”). */
	label: string;
	deviceCount: number;
	onlineCount: number;
	supportedCount: number;
	printerCount: number;
};
export type PharmaciesResponse = {
	minAgentVersion: string;
	pharmacies: PrinterPharmacy[];
	/** Pusta dla ról innych niż super_admin; grupy bez urządzeń są pominięte. */
	groups: PrinterGroup[];
};

/**
 * Miejsce widoku: apteka albo grupa urządzeń bez apteki. W grupie nie ma
 * udostępniania — to nie sieć jednej apteki (backend: 409 `not_same_pharmacy`).
 */
export type PrinterPlace =
	| { kind: "pharmacy"; idfirm: number }
	| { kind: "group"; group: PrinterGroupId };

// GET /device-agent/printers?idfirm= | ?group=
export type PrinterEnv = {
	osBuild: number | null;
	osDisplayVersion: string | null;
	edition: string | null;
	spooler: string;
	lanmanServer: string;
	networkCategory: string;
	firewallRule: string;
	spoolerRemoteRpc: string;
	printAccount: string;
	/** Usługa Zapory Windows (MpsSvc): `running | stopped | disabled | missing | unknown`. */
	firewallService: string;
	eset: { product: string; version: string | null; firewall: boolean } | null;
};
export type PrinterUser = {
	deviceUuid: string;
	displayName: string;
	connectionId: number | null;
	managed: boolean;
	isDefault: boolean;
	userName: string | null;
	/** Połączenie wskazuje adres IP komputera z drukarką, nie jego nazwę. */
	byIp: boolean;
	/** Połączenie po IP, którego komputer z drukarką już nie ma (zmiana przez DHCP). */
	addressStale: boolean;
};
export type ManagedShare = {
	shareId: number;
	shareName: string;
	preexistingShare: boolean;
	createdAt: string;
};
/** Zadania w kolejce (agent ≥ `fixMinAgentVersion`); `error` = pierwsze zadanie stoi z błędem. */
export type PrinterJobs = { count: number; oldestAt: string | null; error: boolean };
/** Adres portu Standard TCP/IP — tylko taką kolejkę da się zainstalować ponownie. */
export type PrinterAddress = {
	host: string;
	protocol: NetworkPrinterProtocol;
	port: number;
	queue: string | null;
};
export type PrinterItem = {
	name: string;
	port: string;
	portKind: string;
	cable: boolean;
	driver: string | null;
	shared: boolean;
	shareName: string | null;
	offline: boolean;
	/** Kolejka wstrzymana w Windows. */
	paused: boolean;
	/** `null` = starszy agent (bez danych o zadaniach). */
	jobs: PrinterJobs | null;
	address: PrinterAddress | null;
	/** Monitor portu (agent ≥ 0.1.128). */
	portMonitor?: string | null;
	/** Cel portu innego monitora (`portKind: "other"`), np. serwer wydruku routera (agent ≥ 0.1.128). */
	portHost?: string | null;
	isDefault: boolean;
	managedShare: ManagedShare | null;
	usedBy: PrinterUser[];
};
export type DeviceConnection = {
	path: string;
	userName: string | null;
	isDefault: boolean;
	hostDeviceUuid: string | null;
	hostDisplayName: string | null;
	printerName: string | null;
	connectionId: number | null;
	managed: boolean;
	/** Ścieżka `\\<IP>\udział` zamiast `\\<NAZWA>\udział`. */
	byIp: boolean;
	/** Adresu z `path` nie ma już wśród bieżących adresów komputera z drukarką. */
	addressStale: boolean;
};
export type UnsupportedReason = "agent_outdated" | "no_inventory";
export type PrinterDevice = {
	deviceUuid: string;
	displayName: string;
	hostname: string | null;
	deviceRole: string;
	online: boolean;
	serviceVersion: string | null;
	supported: boolean;
	unsupportedReason: UnsupportedReason | null;
	inventoryAt: string | null;
	/** Agent ≥ `removeMinAgentVersion` z inwentarzem — przyjmie `printer_remove`. */
	removeSupported: boolean;
	/** Agent ≥ `addMinAgentVersion` i inwentarz z `network` — przyjmie `printer_discover`/`printer_add`. */
	addSupported: boolean;
	/** Agent ≥ `fixMinAgentVersion` z inwentarzem — przyjmie naprawę kolejki, reset bufora i reinstalację. */
	fixSupported: boolean;
	/** Sterowniki do ponownej instalacji: zainstalowane na komputerze + ogólne Microsoft. */
	availableDrivers: string[];
	env: PrinterEnv | null;
	printers: PrinterItem[];
	connections: DeviceConnection[];
};
export type PharmacyPrintersResponse = {
	/** `null` dla grupy. */
	idfirm: number | null;
	group: PrinterGroupId | null;
	/** Nazwa apteki albo etykieta grupy. */
	pharmacyName: string | null;
	minAgentVersion: string;
	/** Najstarszy agent, który usuwa drukarki i połączenia (wyższy niż `minAgentVersion`). */
	removeMinAgentVersion: string;
	/** Najstarszy agent, który wykrywa i dodaje drukarki sieciowe. */
	addMinAgentVersion: string;
	/** Najstarszy agent, który naprawia kolejki i bufor wydruku. */
	fixMinAgentVersion: string;
	devices: PrinterDevice[];
	runningOperation: PrinterOperation | null;
};

// operacje
export type OperationStepStatus = "pending" | "running" | "ok" | "failed" | "skipped";
export type OperationStep = {
	step: string;
	status: OperationStepStatus;
	code: string | null;
	win32: number | null;
	detail: string | null;
};
export type OperationKind =
	| "share_connect"
	| "repair"
	| "disconnect"
	| "unshare"
	| "forget"
	| "remove_printer"
	| "remove_connection"
	| "add_printer"
	| "fix_printer"
	| "reset_spooler"
	| "reinstall_printer"
	| "set_default";
export type OperationStatus = "running" | "succeeded" | "failed";
export type PrinterOperation = {
	operationId: number;
	/** `null` dla operacji w grupie. */
	idfirm: number | null;
	group: string | null;
	kind: OperationKind;
	status: OperationStatus;
	cause: string | null;
	errorDetail: string | null;
	hostDeviceUuid: string | null;
	hostDisplayName: string | null;
	clientDeviceUuid: string | null;
	clientDisplayName: string | null;
	printerName: string | null;
	shareId: number | null;
	connectionId: number | null;
	actorName: string | null;
	createdAt: string;
	finishedAt: string | null;
	steps: OperationStep[];
};
export type OperationsResponse = { operations: PrinterOperation[] };
/** 202 z każdego startu operacji. */
export type OperationStarted = { operationId: number };
export type RefreshResponse = { sent: number };

// Dodawanie drukarki sieciowej (agent ≥ addMinAgentVersion)
/** Karta z bramą domyślną komputera; `subnet` wylicza backend (`192.168.11.0/24`). */
export type NetworkInfo = {
	ip: string;
	prefix: number;
	subnet: string;
	gateway: string;
	gatewayMac: string | null;
};
export type NetworkPrinterDriver = {
	name: string;
	/** Sterownik jest już w systemie komputera docelowego. */
	installed: boolean;
	/** Na ilu komputerach tej sieci drukuje nim ta drukarka. */
	usedBy: number;
	/** Ogólny sterownik Microsoft (PCL6/PS) — drukuje bez funkcji producenta. */
	generic: boolean;
};
export type NetworkPrinterVia = "wsd" | "mdns" | "manual";
export type NetworkPrinterProtocol = "raw" | "lpr";
export type NetworkPrinterCandidate = {
	host: string;
	protocol: NetworkPrinterProtocol;
	port: number;
	queue: string | null;
	suggestedName: string;
	model: string | null;
	sources: {
		deviceCount: number;
		/** ≤ 5 nazw urządzeń-źródeł. */
		devices: string[];
		/** Pusta = tylko z inwentarzy sąsiadów. */
		via: NetworkPrinterVia[];
	};
	/** Kolejność backendu: pierwszy = domyślny. */
	drivers: NetworkPrinterDriver[];
	/** Kolejka komputera z tym samym adresem — drukarka już jest zainstalowana. */
	installedAs: string | null;
};
export type DiscoveryState = {
	status: "none" | "running" | "done" | "failed";
	requestedAt: string | null;
	finishedAt: string | null;
	error: string | null;
	checked: { host: string; open: boolean } | null;
};
export type NetworkPrintersResponse = {
	deviceUuid: string;
	addSupported: boolean;
	addMinAgentVersion: string;
	unsupportedReason: "agent_outdated" | "no_inventory" | "no_network" | null;
	online: boolean;
	network: NetworkInfo | null;
	discovery: DiscoveryState;
	printers: NetworkPrinterCandidate[];
};
/** 202 z `POST …/discover`. */
export type DiscoverResponse = { discovery: DiscoveryState };
export type AddPrinterInput = {
	deviceUuid: string;
	host: string;
	protocol: NetworkPrinterProtocol;
	port: number;
	/** Tylko dla `lpr`. */
	queue: string | null;
	printerName: string;
	driverName: string;
	setDefault: boolean;
};

// Naprawa drukarki (agent ≥ fixMinAgentVersion)
/** Co najmniej jedno z `purge` i `resume` (inaczej 400). */
export type FixPrinterInput = {
	deviceUuid: string;
	printerName: string;
	purge: boolean;
	resume: boolean;
};
/** Adres, port i właściciela domyślnej backend bierze z inwentarza. */
export type ReinstallPrinterInput = {
	deviceUuid: string;
	printerName: string;
	driverName: string;
};
/**
 * `printerName` = nazwa kolejki (`printers[].name`) albo ścieżka połączenia
 * (`connections[].path`) tego komputera; użytkownika Windows backend bierze z inwentarza.
 */
export type SetDefaultInput = { deviceUuid: string; printerName: string };

export type ShareInput = {
	hostDeviceUuid: string;
	printerName: string;
	clientDeviceUuid: string;
	setDefault: boolean;
};

/**
 * `newDefault` — nowa drukarka domyślna, gdy usuwana pozycja jest domyślna
 * (nazwa kolejki albo ścieżka połączenia); `null` = Windows wybierze sam.
 */
export type RemovePrinterInput = {
	deviceUuid: string;
	printerName: string;
	newDefault: string | null;
};
export type RemoveConnectionInput = {
	deviceUuid: string;
	path: string;
	userName: string;
	newDefault: string | null;
};

/** Historia apteki, grupy albo całego panelu jest ograniczona po stronie backendu do 200 najnowszych. */
export const OPERATIONS_HISTORY_LIMIT = 200;

/**
 * Miejsce w zapytaniu i w ciele: dokładnie jedno z `idfirm` i `group` — backend
 * odrzuca oba naraz (400), a body odświeżenia ma `deny_unknown_fields`.
 */
function placeParams(place: PrinterPlace): { idfirm: number } | { group: PrinterGroupId } {
	return place.kind === "pharmacy" ? { idfirm: place.idfirm } : { group: place.group };
}

function placeQuery(place: PrinterPlace): URLSearchParams {
	return new URLSearchParams(
		place.kind === "pharmacy" ? { idfirm: String(place.idfirm) } : { group: place.group },
	);
}

export function listPrinterPharmacies(): Promise<PharmaciesResponse> {
	return apiFetch<PharmaciesResponse>("/device-agent/printers/pharmacies");
}

export function getPharmacyPrinters(place: PrinterPlace): Promise<PharmacyPrintersResponse> {
	return apiFetch<PharmacyPrintersResponse>(
		`/device-agent/printers?${placeQuery(place).toString()}`,
	);
}

export function startPrinterShare(input: ShareInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/share", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

/**
 * `forget: true` tylko dla stanowiska offline albo bez obsługi — backend
 * zapomina połączenie w bazie, nie dotykając komputera.
 */
export function disconnectPrinterConnection(
	connectionId: number,
	forget: boolean,
): Promise<OperationStarted> {
	return apiFetch<OperationStarted>(
		`/device-agent/printers/connections/${encodeURIComponent(String(connectionId))}/disconnect`,
		{ method: "POST", body: JSON.stringify({ forget }) },
	);
}

/**
 * Przepina zarządzane połączenie na bieżący adres komputera z drukarką (nazwa,
 * a gdy się nie rozwiązuje — nowy IP) i usuwa stare. Po zmianie adresu przez DHCP.
 */
export function repairPrinterConnection(connectionId: number): Promise<OperationStarted> {
	return apiFetch<OperationStarted>(
		`/device-agent/printers/connections/${encodeURIComponent(String(connectionId))}/repair`,
		{ method: "POST", body: JSON.stringify({}) },
	);
}

export function unsharePrinter(shareId: number): Promise<OperationStarted> {
	return apiFetch<OperationStarted>(
		`/device-agent/printers/shares/${encodeURIComponent(String(shareId))}/unshare`,
		{ method: "POST", body: JSON.stringify({}) },
	);
}

/** Usuwa lokalną kolejkę Windows (agent ≥ `removeMinAgentVersion`). */
export function removePrinter(input: RemovePrinterInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/remove", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

/** Usuwa ręczne połączenie sieciowe użytkownika (spoza modułu) z jego profilu Windows. */
export function removePrinterConnection(input: RemoveConnectionInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/remove-connection", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

/**
 * Wykrywanie drukarek w sieci komputera (WS-Discovery + mDNS, ok. 3 s); z
 * `checkHost` agent sprawdza też TCP 9100 jednego adresu. Wynik przychodzi
 * asynchronicznie — panel odpytuje `getNetworkPrinters`.
 */
export function discoverNetworkPrinters(
	deviceUuid: string,
	checkHost?: string,
): Promise<DiscoverResponse> {
	return apiFetch<DiscoverResponse>(
		`/device-agent/printers/devices/${encodeURIComponent(deviceUuid)}/discover`,
		{ method: "POST", body: JSON.stringify(checkHost ? { checkHost } : {}) },
	);
}

/** Drukarki sieciowe do dodania: z inwentarzy tej samej sieci i z wykrywania. */
export function getNetworkPrinters(deviceUuid: string): Promise<NetworkPrintersResponse> {
	return apiFetch<NetworkPrintersResponse>(
		`/device-agent/printers/devices/${encodeURIComponent(deviceUuid)}/network-printers`,
	);
}

/** Zakłada port Standard TCP/IP i kolejkę Windows na komputerze (operacja `add_printer`). */
export function addNetworkPrinter(input: AddPrinterInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/add", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

/** Wznowienie (tryb offline, pauza) i/lub wyczyszczenie kolejki (operacja `fix_printer`). */
export function fixPrinter(input: FixPrinterInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/fix", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

/**
 * Restart usługi Bufor wydruku z usunięciem zawieszonych zadań WSZYSTKICH
 * drukarek komputera (`reset_spooler`); backend wymaga jawnego `confirm`.
 */
export function resetSpooler(deviceUuid: string): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/reset-spooler", {
		method: "POST",
		body: JSON.stringify({ deviceUuid, confirm: true }),
	});
}

/** Usuwa i zakłada od nowa kolejkę sieciową TCP/IP (`reinstall_printer`). */
export function reinstallPrinter(input: ReinstallPrinterInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/reinstall", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

/** Drukarka domyślna jedynego zalogowanego użytkownika Windows komputera (`set_default`). */
export function setDefaultPrinter(input: SetDefaultInput): Promise<OperationStarted> {
	return apiFetch<OperationStarted>("/device-agent/printers/set-default", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

export function refreshPharmacyPrinters(place: PrinterPlace): Promise<RefreshResponse> {
	return apiFetch<RefreshResponse>("/device-agent/printers/refresh", {
		method: "POST",
		body: JSON.stringify(placeParams(place)),
	});
}

/** Bez miejsca — historia całego panelu: wszystkie widoczne apteki (grupy: super_admin). */
export function listPrinterOperations(place: PrinterPlace | null): Promise<OperationsResponse> {
	const params = place ? placeQuery(place) : new URLSearchParams();
	params.set("limit", String(OPERATIONS_HISTORY_LIMIT));
	return apiFetch<OperationsResponse>(`/device-agent/printers/operations?${params.toString()}`);
}

export function getPrinterOperation(operationId: number): Promise<PrinterOperation> {
	return apiFetch<PrinterOperation>(
		`/device-agent/printers/operations/${encodeURIComponent(String(operationId))}`,
	);
}
